import GolfIcon from '@/components/GolfIcon';
import { mediumImpact } from '@/utils/capacitorUtils';
import { calculateDistance, findPointAtDistance, formatDistance } from '@/utils/geometryUtils';
import { Camera, CameraResultType } from '@capacitor/camera';
import { Geolocation, Position } from '@capacitor/geolocation';
import {
  IonIcon,
  IonToast
} from '@ionic/react';
import { cameraOutline, golf, personCircleOutline } from 'ionicons/icons';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useEffect, useRef, useState } from 'react';

interface MapViewProps {
  activeHole: number;
  holeData: Array<{ hole: number; par: number; distance: number; geodata: any }>;
  navigateToHole: (holeNumber: number) => void;
  goToScoreView: () => void;
  handleLeaveGame: () => void;
  players?: Array<{ id: string; name: string; avatar?: string }>;
}

const GameMapView: React.FC<MapViewProps> = ({
  activeHole,
  holeData,
  navigateToHole,
  goToScoreView,
  players = []
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const [mapLoaded, setMapLoaded] = useState(false);

  // State for location error toast
  const [showToast, setShowToast] = useState(false);
  const [locationError, setLocationError] = useState('');

  // State for coordinates of draggable first point
  const [firstPointCoords, setFirstPointCoords] = useState<[number, number] | null>(null);

  // State for distance to hole (static, could be dynamic)
  const [holeDistance, setHoleDistance] = useState(0);

  // Handle user location
  const handleLocateUser = async () => {
    try {
      mediumImpact();
      if (!mapRef.current) return;
      const permissionStatus = await Geolocation.checkPermissions();
      if (permissionStatus.location !== 'granted') {
        const requestResult = await Geolocation.requestPermissions();
        if (requestResult.location !== 'granted') {
          setLocationError('Location permission is required to use this feature');
          setShowToast(true);
          return;
        }
      }
      const position: Position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
      });
      mapRef.current?.flyTo({
        center: [position.coords.longitude, position.coords.latitude],
        zoom: 18,
        essential: true
      });
    } catch (error) {
      console.error('Error getting location:', error);
      setLocationError('Failed to get your location');
      setShowToast(true);
    }
  };

  // Camera handler
  const handleCameraClick = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
      });
      console.log('Camera photo result:', image);
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };

  // ---------- MAP INITIALIZATION -----------
  useEffect(() => {
    if (!mapContainer.current || !holeData[activeHole - 1]?.geodata) return;
    mapboxgl.accessToken = 'pk.eyJ1IjoieGRpc3A1MDkiLCJhIjoiY20xcndxbXJvMDA2djJtczd6cDAxczR6diJ9.jbNicBwTcJuu0XHVIzZO6w';

    // Remove previous map if exists (for hot reloads)
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    setMapLoaded(false);
    const currentHoleData = holeData[activeHole - 1]?.geodata;
    if (!currentHoleData || !currentHoleData.length) return;

    // Set initial draggable first point
    setFirstPointCoords([currentHoleData[0].lng, currentHoleData[0].lat]);

    // Create map
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/standard-satellite',
      center: [currentHoleData[0].lng, currentHoleData[0].lat],
      zoom: 16,
      attributionControl: true,
      // Add these options to improve performance and prevent style loading issues
      refreshExpiredTiles: false,
      failIfMajorPerformanceCaveat: false,
      preserveDrawingBuffer: true,
      antialias: false,
      trackResize: true
    });
    mapRef.current = map;

    // Handle map load and style loading
    const onMapLoad = () => {
      // Wait for the style to be fully loaded
      if (!map.isStyleLoaded()) {
        map.once('style.load', () => {
          setMapLoaded(true);
          map.resize();
        });
      } else {
        setMapLoaded(true);
        map.resize();
      }
    };

    // Set up load event
    if (map.loaded()) {
      onMapLoad();
    } else {
      map.once('load', onMapLoad);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [activeHole]);

  // ----------- DRAW/UPDATE LAYERS & DRAGGABLE POINT ------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const currentHoleData = holeData[activeHole - 1]?.geodata;
    if (!currentHoleData || !mapLoaded || !currentHoleData.length) {
      return;
    }
    setHoleDistance(holeData[activeHole - 1].distance);

    // Wait for style to be fully loaded
    if (!map.isStyleLoaded()) {
      const onStyleLoad = () => {
        map.off('style.load', onStyleLoad);
        drawMapLayers();
      };
      map.on('style.load', onStyleLoad);
      return;
    }

    drawMapLayers();

    function drawMapLayers() {
      if (!map) return;

      // Clean up previous layers/sources
      const layerIds = [
        'hole-path',
        'hole-points',
        'tee-point',     // Renamed from first-point
        'target-point',  // New point for the 230m target
        'flag-point',    // Renamed from last-point
        'distance-labels'
      ];

      layerIds.forEach(id => {
        if (map.getLayer(id)) map.removeLayer(id);
        if (map.getSource(id)) map.removeSource(id);
      });

      console.log('currentHoleData', currentHoleData)

      // Using findPointAtDistance from geometryUtils.ts

      // Get the three key points for our golf hole
      // 1. Tee position (first point - draggable)
      let teePoint = currentHoleData[currentHoleData.length - 1]; // Last point in array is tee

      // 2. Flag position (red dot - last point in the hole)
      let flagPoint = currentHoleData[0]; // First point in array is flag

      // 3. Calculate target point at 230m from tee along the path
      let targetPoint = findPointAtDistance(currentHoleData.slice().reverse(), 230);

      // Create an array with just our three points
      const golfPoints = [
        teePoint,
        targetPoint,
        flagPoint
      ];

      // --- Draw main hole path connecting the three points ---
      map.addSource('hole-path', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: golfPoints.map((pt: { lng: number; lat: number }) => [pt.lng, pt.lat])
          },
          properties: {}
        }
      });

      map.addLayer({
        id: 'hole-path',
        type: 'line',
        source: 'hole-path',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': "#FFFFFF",
          'line-width': 4
        }
      });

      // Add distance labels between our three golf points
      // Create a feature collection for the distance labels
      const distanceFeatures = [];

      // We only need two distance labels: tee to target, and target to flag
      for (let i = 0; i < golfPoints.length - 1; i++) {
        const start = golfPoints[i];
        const end = golfPoints[i + 1];

        // Calculate midpoint for label placement
        const midpoint = [
          (start.lng + end.lng) / 2,
          (start.lat + end.lat) / 2
        ];

        // Calculate distance between points
        const distance = calculateDistance(
          start.lat, start.lng,
          end.lat, end.lng
        );

        // Format distance for display
        const distanceText = formatDistance(distance);

        // Add feature for this distance label
        distanceFeatures.push({
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: midpoint as [number, number]
          },
          properties: {
            description: distanceText
          }
        });
      }

      // Add source for distance labels
      map.addSource('distance-labels', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: distanceFeatures
        }
      });

      // Create a rounded rectangle image for our labels
      const createRoundedRectImage = () => {
        const width = 100;
        const height = 60;
        const radius = 15; // Corner radius

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          // Draw rounded rectangle
          ctx.beginPath();
          ctx.moveTo(radius, 0);
          ctx.lineTo(width - radius, 0);
          ctx.quadraticCurveTo(width, 0, width, radius);
          ctx.lineTo(width, height - radius);
          ctx.quadraticCurveTo(width, height, width - radius, height);
          ctx.lineTo(radius, height);
          ctx.quadraticCurveTo(0, height, 0, height - radius);
          ctx.lineTo(0, radius);
          ctx.quadraticCurveTo(0, 0, radius, 0);
          ctx.closePath();

          // Fill with white
          ctx.fillStyle = '#ffffff';
          ctx.fill();
        }

        return canvas.toDataURL();
      };

      // Add the rounded rectangle image to the map
      const roundedRectUrl = createRoundedRectImage();
      const roundedRectImage = new Image();
      roundedRectImage.onload = () => {
        if (map) {
          map.addImage('rounded-rect', roundedRectImage);

          // Add a background layer using the custom image
          map.addLayer({
            id: 'distance-labels-bg',
            type: 'symbol',
            source: 'distance-labels',
            layout: {
              'icon-image': 'rounded-rect',
              'icon-size': 1,
              'icon-text-fit': 'both',
              'icon-text-fit-padding': [8, 12, 8, 12], // Top, right, bottom, left padding
              'icon-allow-overlap': true,
              'text-field': ['get', 'description'],
              'text-size': 18,
              'text-offset': [0, 0],
              'text-anchor': 'center',
              'text-allow-overlap': true,
            },
            paint: {
              'text-color': '#000000', // Transparent text (we'll show it in the next layer)
            }
          });
        }
      };
      roundedRectImage.src = roundedRectUrl;

      // Then add the text layer on top
      map.addLayer({
        id: 'distance-labels',
        type: 'symbol',
        source: 'distance-labels',
        layout: {
          'text-field': ['get', 'description'],
          'text-size': 26,
          'text-offset': [0, 0],
          'text-anchor': 'center',
          'text-allow-overlap': true,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold']
        },
        paint: {
          'text-color': '#000000'
        }
      });

      // --- Draw flag point as RED circle ---
      map.addSource('flag-point', {
        type: 'geojson',
        data: {
          type: "FeatureCollection" as const,
          features: [
            {
              type: 'Feature' as const,
              geometry: {
                type: 'Point' as const,
                coordinates: [flagPoint.lng, flagPoint.lat] as [number, number]
              },
              properties: {}
            }
          ]
        }
      });

      map.addLayer({
        id: 'flag-point',
        type: 'circle',
        source: 'flag-point',
        paint: {
          'circle-radius': 8,
          'circle-color': '#F84C4C',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 3
        }
      });

      // --- Draw target point as GREEN circle (draggable) ---
      const targetCoords: [number, number] = [targetPoint.lng, targetPoint.lat];
      map.addSource('target-point', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature' as const,
              geometry: {
                type: 'Point' as const,
                coordinates: targetCoords
              },
              properties: {}
            }
          ]
        }
      });

      map.addLayer({
        id: 'target-point',
        type: 'circle',
        source: 'target-point',
        paint: {
          'circle-radius': 16,
          'circle-color': '#fff',
          'circle-stroke-color': '#22c55e',
          'circle-stroke-width': 3
        }
      });

      // --- Draw tee point as draggable blue circle ---
      const teeCoords: [number, number] = [teePoint.lng, teePoint.lat];
      console.log('teeCoords', teeCoords);

      // Add/Update draggable source+layer for tee point
      map.addSource('tee-point', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature' as const,
              geometry: {
                type: 'Point' as const,
                coordinates: teeCoords
              },
              properties: {}
            }
          ]
        }
      });

      map.addLayer({
        id: 'tee-point',
        type: 'circle',
        source: 'tee-point',
        paint: {
          'circle-radius': 10,
          'circle-color': '#4668F2',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 2
        }
      });

      // Enable dragging for target-point
      let isDragging = false;
      let canvas = map.getCanvasContainer();

      function onMove(e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) {
        if (!isDragging || !map) return;
        const coords = e.lngLat;
        const newCoords: [number, number] = [coords.lng, coords.lat];

        // Update targetPoint state variable during drag
        targetPoint = { lat: coords.lat, lng: coords.lng };

        // Update the target point position
        const targetSource = map.getSource('target-point') as mapboxgl.GeoJSONSource;
        if (targetSource) {
          targetSource.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature' as const,
              geometry: {
                type: 'Point' as const,
                coordinates: newCoords
              },
              properties: {}
            }]
          });
        }

        // Update the path line to follow the point
        const pathSource = map.getSource('hole-path') as mapboxgl.GeoJSONSource;
        if (pathSource) {
          pathSource.setData({
            type: 'Feature' as const,
            geometry: {
              type: 'LineString' as const,
              coordinates: [
                [teePoint.lng, teePoint.lat],
                newCoords,
                [flagPoint.lng, flagPoint.lat]
              ]
            },
            properties: {}
          });
        }

        // Update distance labels in real-time
        updateDistanceLabels();

        canvas.style.cursor = 'grabbing';
      }

      // Helper function to update distance labels in real-time
      function updateDistanceLabels() {
        if (!map) return;

        const distanceFeatures = [];
        const golfPoints = [
          teePoint,
          targetPoint,
          flagPoint
        ];

        for (let i = 0; i < golfPoints.length - 1; i++) {
          const start = golfPoints[i];
          const end = golfPoints[i + 1];

          // Calculate midpoint for label placement
          const midpoint = [
            (start.lng + end.lng) / 2,
            (start.lat + end.lat) / 2
          ];

          // Calculate distance between points
          const distance = calculateDistance(
            start.lat, start.lng,
            end.lat, end.lng
          );

          // Format distance for display
          const distanceText = formatDistance(distance);

          // Add feature for this distance label
          distanceFeatures.push({
            type: 'Feature' as const,
            geometry: {
              type: 'Point' as const,
              coordinates: midpoint as [number, number]
            },
            properties: {
              description: distanceText
            }
          });
        }

        // Update the distance labels source
        const labelsSource = map.getSource('distance-labels') as mapboxgl.GeoJSONSource;
        if (labelsSource) {
          labelsSource.setData({
            type: 'FeatureCollection',
            features: distanceFeatures
          });
        }
      }

      function onUp(e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) {
        if (!isDragging) return;
        isDragging = false;
        const coords = e.lngLat;
        // Update target point coordinates (already updated in onMove)
        targetPoint = { lat: coords.lat, lng: coords.lng };
        // Final update of distance labels
        updateDistanceLabels();

        canvas.style.cursor = '';
        map.off('mousemove', onMove);
        map.off('touchmove', onMove);
      }

      map.on('mouseenter', 'target-point', () => {
        map.setPaintProperty('target-point', 'circle-color', '#3887be');
        canvas.style.cursor = 'move';
      });

      map.on('mouseleave', 'target-point', () => {
        map.setPaintProperty('target-point', 'circle-color', '#3bb2d0');
        canvas.style.cursor = '';
      });

      map.on('mousedown', 'target-point', (e) => {
        e.preventDefault();
        isDragging = true;
        canvas.style.cursor = 'grab';
        map.on('mousemove', onMove);
        map.once('mouseup', onUp);
      });

      map.on('touchstart', 'target-point', (e) => {
        if (e.points.length !== 1) return;
        e.preventDefault();
        isDragging = true;
        map.on('touchmove', onMove);
        map.once('touchend', onUp);
      });

      // Enable dragging for tee-point
      let isDraggingTee = false;

      function onMoveTee(e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) {
        if (!isDraggingTee || !map) return;
        const coords = e.lngLat;
        const newCoords: [number, number] = [coords.lng, coords.lat];

        // Update teePoint state variable during drag
        teePoint = { lat: coords.lat, lng: coords.lng };

        // Update the tee point position
        const teeSource = map.getSource('tee-point') as mapboxgl.GeoJSONSource;
        if (teeSource) {
          teeSource.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature' as const,
              geometry: {
                type: 'Point' as const,
                coordinates: newCoords
              },
              properties: {}
            }]
          });
        }

        // Update the path line to follow the point
        const pathSource = map.getSource('hole-path') as mapboxgl.GeoJSONSource;
        if (pathSource) {
          pathSource.setData({
            type: 'Feature' as const,
            geometry: {
              type: 'LineString' as const,
              coordinates: [
                newCoords,
                [targetPoint.lng, targetPoint.lat],
                [flagPoint.lng, flagPoint.lat]
              ]
            },
            properties: {}
          });
        }

        // Update distance labels in real-time
        updateDistanceLabels();

        canvas.style.cursor = 'grabbing';
      }

      function onUpTee(e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) {
        if (!isDraggingTee) return;
        isDraggingTee = false;
        const coords = e.lngLat;
        // Update tee point coordinates (already updated in onMoveTee)
        teePoint = { lat: coords.lat, lng: coords.lng };
        // Final update of distance labels
        updateDistanceLabels();

        canvas.style.cursor = '';
        map.off('mousemove', onMoveTee);
        map.off('touchmove', onMoveTee);
      }

      map.on('mouseenter', 'tee-point', () => {
        map.setPaintProperty('tee-point', 'circle-color', '#3887be');
        canvas.style.cursor = 'move';
      });

      map.on('mouseleave', 'tee-point', () => {
        map.setPaintProperty('tee-point', 'circle-color', '#3bb2d0');
        canvas.style.cursor = '';
      });

      map.on('mousedown', 'tee-point', (e) => {
        e.preventDefault();
        isDraggingTee = true;
        canvas.style.cursor = 'grab';
        map.on('mousemove', onMoveTee);
        map.once('mouseup', onUpTee);
      });

      map.on('touchstart', 'tee-point', (e) => {
        if (e.points.length !== 1) return;
        e.preventDefault();
        isDraggingTee = true;
        map.on('touchmove', onMoveTee);
        map.once('touchend', onUpTee);
      });

      // Using geometry utilities from geometryUtils.ts

      // Cleanup function
      return () => {
        if (!map || !map.getStyle) return;

        try {
          const layerIds = [
            'hole-path',
            'hole-points',
            'first-point',
            'last-point',
            'distance-labels'
          ];

          layerIds.forEach(id => {
            if (map.getLayer(id)) map.removeLayer(id);
            if (map.getSource(id)) map.removeSource(id);
          });
        } catch (error) {
          console.error('Error cleaning up map layers:', error);
        }
      };
    }

    // We don't need to draw the intermediate points anymore, just our three key points

    map.addSource('hole-points', {
      type: 'geojson',
      data: {
        type: "FeatureCollection",
        features: []
      }
    });

    map.addLayer({
      id: 'hole-points',
      type: 'circle',
      source: 'hole-points',
      paint: {
        'circle-radius': 7,
        'circle-color': '#22c55e',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2
      }
    });

    // --- Draw first point as RED circle (last point in the hole) ---
    const firstPoint = currentHoleData[0]; // This is now the last point (red dot)
    const lastPoint = currentHoleData[currentHoleData.length - 1];

    map.addSource('last-point', {
      type: 'geojson',
      data: {
        type: "FeatureCollection" as const,
        features: [
          {
            type: 'Feature' as const,
            geometry: {
              type: 'Point' as const,
              coordinates: [firstPoint.lng, firstPoint.lat] as [number, number]
            },
            properties: {}
          }
        ]
      }
    });

    map.addLayer({
      id: 'last-point',
      type: 'circle',
      source: 'last-point',
      paint: {
        'circle-radius': 8,
        'circle-color': '#F84C4C',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 3
      }
    });

    // --- Draw last point as draggable blue circle (first point in the hole) ---
    const draggableCoords: [number, number] = [lastPoint.lng, lastPoint.lat];
    console.log('draggableCoords', draggableCoords)
    // We don't need to draw the intermediate points anymore, just our three key points

    // Enable dragging for first-point
    let isDragging = false;
    let canvas = map.getCanvasContainer();

    function onMove(e: mapboxgl.MapMouseEvent & mapboxgl.EventData) {
      if (!isDragging) return;
      const coords = e.lngLat;
      map.getSource('first-point').setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [coords.lng, coords.lat] },
          properties: {}
        }]
      });
      canvas.style.cursor = 'grabbing';
    }

    function onUp(e: mapboxgl.MapMouseEvent & mapboxgl.EventData) {
      if (!isDragging) return;
      isDragging = false;
      const coords = e.lngLat;
      setFirstPointCoords([coords.lng, coords.lat]);
      canvas.style.cursor = '';
      map.off('mousemove', onMove);
      map.off('touchmove', onMove);
    }

    map.on('mouseenter', 'first-point', () => {
      map.setPaintProperty('first-point', 'circle-color', '#3887be');
      canvas.style.cursor = 'move';
    });

    map.on('mouseleave', 'first-point', () => {
      map.setPaintProperty('first-point', 'circle-color', '#3bb2d0');
      canvas.style.cursor = '';
    });

    map.on('mousedown', 'first-point', (e) => {
      e.preventDefault();
      isDragging = true;
      canvas.style.cursor = 'grab';
      map.on('mousemove', onMove);
      map.once('mouseup', onUp);
    });

    map.on('touchstart', 'first-point', (e) => {
      if (e.points.length !== 1) return;
      e.preventDefault();
      isDragging = true;
      map.on('touchmove', onMove);
      map.once('touchend', onUp);
    });

    // Cleanup function
    return () => {
      if (!map || !map.getStyle) return;

      try {
        const layerIds = [
          'hole-path',
          'hole-points',
          'first-point',
          'last-point',
          'distance-labels'
        ];

        layerIds.forEach(id => {
          if (map.getLayer(id)) map.removeLayer(id);
          if (map.getSource(id)) map.removeSource(id);
        });

        // Remove event listeners - using any type to avoid TypeScript errors with mapbox event types
        const mapAny = map as any;
        mapAny.off('mouseenter');
        mapAny.off('mouseleave');
        mapAny.off('mousedown');
        mapAny.off('touchstart');
        mapAny.off('mousemove');
        mapAny.off('touchmove');
        mapAny.off('mouseup');
        mapAny.off('touchend');
        mapAny.off('style.load');
      } catch (error) {
        console.error('Error cleaning up map layers:', error);
      }
    };
  }, [activeHole, holeData, firstPointCoords]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div
        ref={mapContainer}
        className="absolute inset-0 size-full"
        style={{ width: '100vw', height: '100vh' }}
      ></div>
      {/* Toast for location errors */}
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={locationError || 'An error occurred'}
        duration={3000}
        position="top"
        color="danger"
      />
      {/* Top left status bar */}
      <div className="absolute top-safe left-0 right-0 flex justify-start items-center p-2 text-white z-10">
        <div className="flex items-center gap-1 bg-card/90 px-2 py-1 rounded-md shadow-md">
          <IonIcon icon={golf} className="text-primary" />
          <span className="font-bold text-foreground">{holeDistance}<span className="text-xs align-top ml-0.5">m</span></span>
        </div>
      </div>
      {/* Left side player avatars with scores */}
      <div className="absolute left-2 top-safe hidden flex-col gap-2 z-10">
        {
          players.map((player, index) => (
            <div key={player.id} className={`flex flex-col items-center ${index === 0 ? '' : `opacity-${80 - index * 20}`}`}>
              <div className={`size-10 rounded-full overflow-hidden shadow-md border-2 ${index === 0 ? 'border-primary' : 'border-secondary'}`}>
                {player.avatar ? (
                  <img src={player.avatar} alt={player.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <IonIcon icon={personCircleOutline} className="text-xl text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>
          ))
        }
      </div>
      {/* Right side tools */}
      <div className="absolute right-2 top-safe space-y-2 flex flex-col z-10 shadow-md overflow-hidden">
        <div className="bg-card rounded-xl">
          <button className="p-2 flex items-center justify-center border-b border-border" onClick={handleLocateUser}>
            <GolfIcon name="navigation" className="text-foreground text-xl" />
          </button>
          <button className="p-2 flex items-center justify-center border-b border-border">
            <GolfIcon name="layer" className="text-foreground text-xl" />
          </button>
          <button className="p-2 flex items-center justify-center hover:bg-accent border-b border-border" onClick={handleCameraClick}>
            <IonIcon icon={cameraOutline} className="text-foreground text-xl" />
          </button>
          <button className="p-2 flex items-center justify-center hover:bg-accent">
            <GolfIcon name="settings" className="text-foreground text-xl" />
          </button>
        </div>
        <button
          className="p-2 flex bg-card rounded-xl items-center justify-center hover:bg-accent"
          onClick={goToScoreView}
        >
          <GolfIcon name="scorecard" className="text-foreground text-xl" />
        </button>
      </div>
    </div>
  );
};

export default GameMapView;